import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const POLL_MS = 5 * 60 * 1000;
const ESCALATE_WITHIN_MS = 15 * 60 * 1000;
const DISMISSED_KEY = 'dismissedAnnouncementIds';

function readDismissedInfoIds() {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
  } catch (e) {
    return [];
  }
}

function formatCountdown(remainingMs) {
  if (remainingMs <= 0) return 'starting now';
  const minutes = Math.ceil(remainingMs / 60000);
  if (remainingMs < 60000) return 'starts in under a minute';
  return `starts in ${minutes} min`;
}

/**
 * Thin one-line platform notice (e.g. scheduled maintenance), shown app-wide including the login
 * page. Quiet and dismissible until the announced event is within 15 minutes, then it turns
 * amber, can't be dismissed, and counts down. INFO dismissals persist per announcement; WARNING
 * dismissals only last until the next page load, so a maintenance notice isn't missed for good.
 */
function AnnouncementStrip() {
  const [announcements, setAnnouncements] = useState([]);
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [nowMs, setNowMs] = useState(Date.now());
  const [dismissedInfoIds, setDismissedInfoIds] = useState(readDismissedInfoIds);
  const [dismissedWarningIds, setDismissedWarningIds] = useState([]);

  const fetchActive = useCallback(async () => {
    try {
      const response = await axios.get('/api/public/announcements/active');
      const serverTime = Date.parse(response.data.serverTime);
      if (!Number.isNaN(serverTime)) {
        setClockOffsetMs(serverTime - Date.now());
      }
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      // Best-effort: keep whatever we already had (e.g. during a redeploy) rather than flashing errors.
    }
  }, []);

  useEffect(() => {
    fetchActive();
    const poll = setInterval(fetchActive, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchActive();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchActive]);

  const serverNow = nowMs + clockOffsetMs;
  const counting = announcements.some((a) => a.eventAt && Date.parse(a.eventAt) - serverNow <= ESCALATE_WITHIN_MS);

  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), counting ? 1000 : 30000);
    return () => clearInterval(tick);
  }, [counting]);

  const dismiss = (announcement) => {
    if (announcement.severity === 'WARNING') {
      setDismissedWarningIds((ids) => [...ids, announcement.id]);
      return;
    }
    const next = [...dismissedInfoIds, announcement.id];
    setDismissedInfoIds(next);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch (e) {
      // Storage unavailable - dismissal just won't persist across reloads.
    }
  };

  const rows = announcements
    .filter((a) => Date.parse(a.endsAt) > serverNow)
    .map((a) => {
      const remaining = a.eventAt ? Date.parse(a.eventAt) - serverNow : null;
      const escalated = remaining !== null && remaining <= ESCALATE_WITHIN_MS;
      return { a, remaining, escalated };
    })
    .filter(({ a, escalated }) => {
      if (escalated) return true;
      return a.severity === 'WARNING' ? !dismissedWarningIds.includes(a.id) : !dismissedInfoIds.includes(a.id);
    });

  if (rows.length === 0) return null;

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 1100 }}>
      {rows.map(({ a, remaining, escalated }) => {
        const tone = escalated
          ? 'bg-warning text-dark fw-semibold'
          : a.severity === 'WARNING'
            ? 'bg-warning-subtle text-warning-emphasis'
            : 'bg-info-subtle text-info-emphasis';
        return (
          <div
            key={a.id}
            role="status"
            className={`d-flex align-items-center justify-content-center gap-2 px-3 ${tone}`}
            style={{ minHeight: 32, fontSize: '0.85rem' }}
          >
            <span>
              {a.message}
              {escalated && ` — ${formatCountdown(remaining)}`}
            </span>
            {!escalated && (
              <button
                type="button"
                className="btn-close btn-close-sm ms-2"
                style={{ fontSize: '0.6rem' }}
                aria-label="Dismiss announcement"
                onClick={() => dismiss(a)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default AnnouncementStrip;
